import { FC, useState } from 'react';
import { html } from 'lit/static-html.js';
import { ColumnDef } from '@tanstack/react-table';
import {
  langs,
  PopoverWC,
  Spacer,
  TextCaption,
  TextTitle,
  Stack,
  Tooltip,
  Icon,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';
import { throwErrorWithId } from '@pypestream/utils';

import { WithLoadingState } from '../router';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { ActionFile, ActionSection, useEditorModal } from '../components';
import { RowSelectionChangeProps } from '../components/action-section/action-section';
import { StudioPageProps } from '../hooks/use-pro-studio-routes';
import {
  uploadFile,
  blobToFile,
  createPythonBlob,
  DataFileType,
  progressServerConfig,
  transformActionFileData,
} from '../utils';
import { ProStudioContext } from '../xstate/prostudio.xstate.types';

type ActionsPageProps = StudioPageProps;

const ActionsPage: FC<ActionsPageProps> = () => {
  const [t] = useTranslation();

  const { functions, configurations, versionId, projectId } =
    useProStudioCtxSelector((ctx) => ({
      projectId: ctx.project?.id,
      versionId:
        ctx.customerId && ctx.botName && ctx.draftVersion
          ? `${ctx.customerId}.${ctx.botName}.${ctx.draftVersion}`
          : undefined,
      configurations:
        ctx.draft?.files
          .filter((file) => /^[^/]+\.py$/.test(file))
          .map(transformActionFileData) || [],
      functions:
        ctx.draft?.files
          .filter((file) => file.startsWith('services/'))
          .map(transformActionFileData) || [],
    }));

  const [progress, setProgress] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fileData, setFileData] = useState<
    | {
        fileName: string;
        fileData: string;
        type: Omit<DataFileType, 'assetData'>;
      }
    | undefined
  >();

  const handleSaveScript = (editorCode: string) => {
    const { fileName, type } = fileData || {};

    if (!projectId) {
      throwErrorWithId('Project ID is required');
    }

    if (!fileName) {
      throwErrorWithId('File name is required');
      return;
    }

    if (!versionId) {
      throwErrorWithId('Version ID is required');
    }

    if (!type) {
      throwErrorWithId('Type is required');
    }

    setIsLoading(true);

    uploadFile({
      url: `${import.meta.env.FE_ADMIN_URL}/rest/bot-manager/${projectId}/${versionId}/${type === 'scriptFile' ? 'scripts' : 'config'}`,
      file: blobToFile(createPythonBlob(editorCode), fileName),
      type: type as DataFileType,
      onChange: ({ data }) => {
        sendProStudioEvent({
          type: 'updateDraftData',
          draft: data as ProStudioContext['draft'],
          action: 'update',
        });

        setIsLoading(false);
      },
      progress: (c, l, total) =>
        progressServerConfig(c, l, total, (p) => setProgress(p)),
    });
  };

  const { editorModal, openModal: openEditorModal } = useEditorModal({
    loading: isLoading,
    progress,
    code: fileData?.fileData,
    title: fileData?.fileName,
    langs: [langs.python()],
    onSave: handleSaveScript,
    onClose: () => {
      setFileData(undefined);
      setProgress(undefined);
      setIsLoading(false);
    },
  });

  const handleOpenEditor = ({ name, path, type }: RowSelectionChangeProps) => {
    openEditorModal();
    sendProStudioEvent({
      type: 'editScript',
      path,
      name,
      cb: (data) =>
        setFileData({
          fileData: data,
          fileName: name,
          type,
        }),
    });
  };

  const configurationsColumns: ColumnDef<ActionFile>[] = [
    {
      accessorKey: 'name',
      header: () =>
        t('pro-studio/actions:section1.label', {
          defaultValue: 'File Name',
        }),
      cell: ({ row }) =>
        html`<ps-text-subtitle size="small">
          ${row?.original?.name}
        </ps-text-subtitle>`,
    },
    {
      id: 'actions',
      header: () =>
        html`<ps-text-body style="opacity: 0">Actions</ps-text-body>`,
      cell: ({ row }) => {
        return html`<ps-stack
          alignItems="center"
          justifyContent="end"
          gutter="xsmall"
          nowrap
        >
          <ps-icon-button
            ?disabled=${row.original.loading || !versionId}
            name="edit"
            size="small"
            @click=${(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleOpenEditor({
                name: row.original.name,
                path: row.original.path,
                type: 'configFile',
              });
            }}
          ></ps-icon-button>
          <ps-icon-button
            ?disabled=${row.original.loading || !versionId}
            name="download"
            size="small"
            @click=${(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              sendProStudioEvent({
                type: 'downloadScript',
                path: row.original.path,
                name: row.original.name,
              });
            }}
          ></ps-icon-button>
        </ps-stack>`;
      },
      meta: {
        align: 'end',
      },
    },
  ];

  const functionsColumns: ColumnDef<ActionFile>[] = [
    {
      accessorKey: 'name',
      header: () =>
        t('pro-studio/actions:section2.label', {
          defaultValue: 'File Name',
        }),
      cell: ({ row }) =>
        html` <ps-text-subtitle size="small">
          ${row?.original?.name}
        </ps-text-subtitle>`,
    },
    {
      id: 'actions',
      header: () =>
        html`<ps-text-body style="opacity: 0">Actions</ps-text-body>`,
      cell: ({ row }) => {
        const popoverId = `delete-popover-${row.original.name}`;
        const popoverTriggerId = `${popoverId}-trigger`;

        const closeDeletePopover = () => {
          const popover = document.getElementById(popoverId) as PopoverWC;
          if (popover) {
            popover.hide();
          }
        };

        return html`<ps-stack
          alignItems="center"
          justifyContent="end"
          gutter="xsmall"
          nowrap
        >
          <ps-icon-button
            ?disabled=${row.original.loading || !versionId}
            name="edit"
            size="small"
            @click=${(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              handleOpenEditor({
                name: row.original.name,
                path: row.original.path,
                type: 'scriptFile',
              });
            }}
          ></ps-icon-button>
          <ps-icon-button
            ?disabled=${row.original.loading || !versionId}
            name="download"
            size="small"
            @click=${(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              sendProStudioEvent({
                type: 'downloadScript',
                path: row.original.path,
                name: row.original.name,
              });
            }}
          ></ps-icon-button>
          <ps-icon-button
            ?disabled=${row.original.loading || !versionId}
            id=${popoverTriggerId}
            name="delete"
            size="small"
            @click=${(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
            }}
          ></ps-icon-button>
          <ps-popover id=${popoverId} width="auto" trigger=${popoverTriggerId}>
            <ps-text-overline variant="tertiary">
              ${t('pro-studio/actions:deleteFilePopover.title', {
                defaultValue: 'Are you sure you want to delete this script?',
              })}
            </ps-text-overline>
            <ps-spacer size="small"></ps-spacer>
            <ps-stack direction="row" justifyContent="space-between" nowrap>
              <ps-button
                variant="secondary"
                size="small"
                width="full"
                @click=${closeDeletePopover}
              >
                ${t('pro-studio/common:deleteFilePopover.cancel', {
                  defaultValue: 'Cancel',
                })}
              </ps-button>
              <ps-button
                variant="danger"
                size="small"
                width="full"
                @click=${() => {
                  sendProStudioEvent({
                    type: 'deleteScript',
                    id: row.original.name,
                  });
                  closeDeletePopover();
                }}
              >
                ${t('pro-studio/common:deleteFilePopover.delete', {
                  defaultValue: 'Delete',
                })}
              </ps-button>
            </ps-stack>
          </ps-popover>
        </ps-stack>`;
      },
      meta: {
        align: 'end',
      },
    },
  ];

  const actions = [
    {
      title: t('pro-studio/actions:section1.title', {
        defaultValue: 'Configuration',
      }),
      label: t('pro-studio/actions:section1.action', {
        defaultValue: 'Upload Config',
      }),
      columns: configurationsColumns,
      data: configurations,
      type: 'configFile',
    },
    {
      title: t('pro-studio/actions:section2.title', {
        defaultValue: 'Functions',
      }),
      label: t('pro-studio/actions:section2.action', {
        defaultValue: 'Upload Scripts',
      }),
      columns: functionsColumns,
      data: functions,
      type: 'scriptFile',
    },
  ];

  return (
    <>
      <Stack gutter="xsmall" alignItems="center">
        <TextTitle test-id="ps-actions-page-title" size="small">
          <TranslationComponent i18nKey="pro-studio/actions:title">
            Actions
          </TranslationComponent>
        </TextTitle>
        <Tooltip test-id="ps-actions-page-info-tooltip" placement="right">
          <Icon
            test-id="ps-actions-page-info-icon"
            name="info"
            color="gray"
            size="small"
          />
          <TextCaption variant="primary" slot="content">
            <TranslationComponent i18nKey="pro-studio/actions:subtitle.part1">
              Actions allow the designer to perform custom integrations and
              special logic. Actions must have corresponding script(s), utils,
              and config files uploaded below. Actions must have a .py
              extension. For more information regarding Actions, please read
            </TranslationComponent>{' '}
            <a href="#">
              <TranslationComponent i18nKey="pro-studio/actions:subtitle.part2">
                the documentation
              </TranslationComponent>
            </a>
            .
          </TextCaption>
        </Tooltip>
      </Stack>
      <Spacer size="large" />
      {actions.map((action) => (
        <ActionSection
          key={action.title}
          {...action}
          onRowSelection={handleOpenEditor}
        />
      ))}
      {editorModal}
    </>
  );
};

export const Actions = WithLoadingState(ActionsPage);
